import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Heart, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CharacterPortrait from "@/components/ascend/CharacterPortrait";

/**
 * ThemedQuoteNudge — dashboard card that surfaces a library quote when the
 * user's mood signals a low day. Reads /api/nudge/themed-quote.
 * - Add-to-collection posts to /api/me/anime/library-add
 * - Dismiss posts to /api/nudge/themed-quote/dismiss
 * Session-only session-storage guard prevents flicker on route changes.
 */
export default function ThemedQuoteNudge() {
  const [quote, setQuote] = useState(null);
  const [reason, setReason] = useState(null);
  const [state, setState] = useState("loading"); // loading | idle | added | dismissed | hidden
  const [busy, setBusy] = useState(false);

  const fetchNudge = async () => {
    try {
      const { data } = await api.get("/nudge/themed-quote");
      if (data.quote) {
        setQuote(data.quote);
        setReason(data.reason);
        setState("idle");
      } else {
        setState("hidden");
      }
    } catch (e) {
      setState("hidden");
    }
  };

  useEffect(() => {
    fetchNudge();
  }, []);

  const dismiss = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      await api.post("/nudge/themed-quote/dismiss", { library_quote_id: quote.id });
      setState("dismissed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/anime/library-add", { library_quote_id: quote.id });
      if (data.reason === "quote_limit_reached") {
        toast.info("You've saved 3 quotes for this anime — clear one first if you want a new favourite.");
        return;
      }
      setState("added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const showAnother = async () => {
    setState("loading");
    setQuote(null);
    // also mark previous as dismissed so we don't get it again this session
    try {
      if (quote?.id) await api.post("/nudge/themed-quote/dismiss", { library_quote_id: quote.id });
    } catch (e) { /* silent */ }
    await fetchNudge();
  };

  if (state === "hidden" || state === "dismissed" || state === "loading") return null;

  if (state === "added") {
    return (
      <div
        className="rounded-3xl px-5 sm:px-6 py-5 mb-6 flex items-center gap-3 fade-up"
        style={{
          background: "color-mix(in oklab, var(--primary-soft) 40%, var(--surface))",
          border: "1px dashed var(--border)",
        }}
        data-testid="themed-quote-nudge-added"
      >
        <Sparkles size={16} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <div className="flex-1 text-sm italic" style={{ color: "var(--text)" }}>
          Added. Would you like another?
        </div>
        <button
          onClick={showAnother}
          disabled={busy}
          className="btn-ghost text-xs"
          data-testid="themed-quote-nudge-another"
        >
          Show me another
        </button>
      </div>
    );
  }

  if (!quote) return null;

  const initial = (quote.character || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded-3xl px-5 sm:px-6 py-5 mb-6 fade-up"
      style={{
        background: "color-mix(in oklab, var(--primary-soft) 32%, var(--surface))",
        border: "1px dashed var(--border)",
      }}
      data-testid="themed-quote-nudge"
      data-nudge-reason={reason || ""}
    >
      <div className="flex items-center gap-2 mb-3">
        <Heart size={12} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span className="eyebrow text-[10px]">you might like this</span>
      </div>
      <div className="flex items-start gap-3 sm:gap-4">
        <CharacterPortrait
          url={quote.character_portrait_url}
          initial={initial}
          size={44}
          testid="themed-quote-nudge-portrait"
        />
        <div className="flex-1 min-w-0">
          <div className="italic leading-snug text-base sm:text-lg" style={{ color: "var(--text)", fontFamily: "serif" }}>
            "{quote.quote_text}"
          </div>
          <div className="text-xs text-muted mt-2">
            — {quote.character} · {quote.anime_title}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
        <button
          onClick={dismiss}
          disabled={busy}
          className="btn-ghost text-sm flex items-center justify-center gap-1"
          data-testid="themed-quote-nudge-dismiss"
        >
          <X size={12} strokeWidth={1.75} /> Not this one
        </button>
        <button
          onClick={add}
          disabled={busy}
          className="btn-primary text-sm flex items-center justify-center gap-2"
          data-testid="themed-quote-nudge-add"
        >
          <Plus size={14} strokeWidth={1.75} /> Add to my collection
        </button>
      </div>
    </div>
  );
}

